<template>
  <LayoutPage>
    <!-- Header Section -->
    <div class="mt-2 flex items-center justify-between mb-6">
      <div>
        <h1 class="text-2xl font-semibold text-gray-900">Events & Data</h1>
        <p class="text-sm text-gray-600 mt-1">Monitor and analyze events recorded by Growlytics marketing automation platform</p>
      </div>
      <div class="flex items-center space-x-3">
        <div class="w-80">
          <el-input v-model="filters.eventName" placeholder="Search events..." size="small" @input="handleSearchChange" clearable>
            <template #prefix>
              <i class="fa fa-search text-gray-400"></i>
            </template>
          </el-input>
        </div>
        <el-button @click="refreshData" :loading="loading" size="small"> <i class="fa fa-refresh mr-2"></i>Refresh </el-button>
      </div>
    </div>

    <!-- Analytics Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div class="bg-white p-4 rounded-lg border">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
              <i class="fa fa-bar-chart text-blue-600"></i>
            </div>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-gray-500">Total Events</p>
            <p class="text-lg font-semibold text-gray-900">{{ formatNumber(filteredEvents.length) }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white p-4 rounded-lg border">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
              <i class="fa fa-calendar text-green-600"></i>
            </div>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-gray-500">This Month Total</p>
            <p class="text-lg font-semibold text-gray-900">{{ formatNumber(getThisMonthTotal()) }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white p-4 rounded-lg border">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
              <i class="fa fa-calendar-o text-purple-600"></i>
            </div>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-gray-500">Last Month Total</p>
            <p class="text-lg font-semibold text-gray-900">{{ formatNumber(getLastMonthTotal()) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Events Table -->
    <div class="bg-white rounded-lg border overflow-hidden">
      <template v-if="loading">
        <div class="p-8 text-center">
          <Spinner />
          <p class="text-gray-500 mt-2">Loading events...</p>
        </div>
      </template>

      <template v-else-if="loadError">
        <LoadErrorTemp @retry="fetchEvents" />
      </template>

      <template v-else>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  @click="sortBy('event')"
                >
                  <div class="flex items-center">
                    Event
                    <i class="fa fa-sort ml-1" v-if="sortField !== 'event'"></i>
                    <i class="fa fa-sort-up ml-1" v-if="sortField === 'event' && sortDirection === 'asc'"></i>
                    <i class="fa fa-sort-down ml-1" v-if="sortField === 'event' && sortDirection === 'desc'"></i>
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  @click="sortBy('this_month')"
                >
                  <div class="flex items-center justify-center">
                    This Month
                    <i class="fa fa-sort ml-1" v-if="sortField !== 'this_month'"></i>
                    <i class="fa fa-sort-up ml-1" v-if="sortField === 'this_month' && sortDirection === 'asc'"></i>
                    <i class="fa fa-sort-down ml-1" v-if="sortField === 'this_month' && sortDirection === 'desc'"></i>
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  @click="sortBy('last_month')"
                >
                  <div class="flex items-center justify-center">
                    Last Month
                    <i class="fa fa-sort ml-1" v-if="sortField !== 'last_month'"></i>
                    <i class="fa fa-sort-up ml-1" v-if="sortField === 'last_month' && sortDirection === 'asc'"></i>
                    <i class="fa fa-sort-down ml-1" v-if="sortField === 'last_month' && sortDirection === 'desc'"></i>
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  @click="sortBy('properties_count')"
                >
                  <div class="flex items-center justify-center">
                    # Of Properties
                    <i class="fa fa-sort ml-1" v-if="sortField !== 'properties_count'"></i>
                    <i class="fa fa-sort-up ml-1" v-if="sortField === 'properties_count' && sortDirection === 'asc'"></i>
                    <i class="fa fa-sort-down ml-1" v-if="sortField === 'properties_count' && sortDirection === 'desc'"></i>
                  </div>
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  @click="sortBy('retention')"
                >
                  <div class="flex items-center justify-center">
                    Data Retention
                    <i class="fa fa-sort ml-1" v-if="sortField !== 'retention'"></i>
                    <i class="fa fa-sort-up ml-1" v-if="sortField === 'retention' && sortDirection === 'asc'"></i>
                    <i class="fa fa-sort-down ml-1" v-if="sortField === 'retention' && sortDirection === 'desc'"></i>
                  </div>
                </th>
                <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <template v-if="sortedEvents.length === 0">
                <tr>
                  <td colspan="6" class="px-6 py-12 text-center">
                    <div class="text-gray-500">
                      <i class="fa fa-inbox text-4xl mb-4 block"></i>
                      <p class="text-lg font-medium">No events found</p>
                      <p class="text-sm">Try adjusting your filters or date range</p>
                    </div>
                  </td>
                </tr>
              </template>

              <tr v-for="(event, index) in sortedEvents" :key="index" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ event.event }}</div>
                </td>

                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="text-sm text-gray-900">{{ formatNumber(event.this_month) }}</div>
                </td>

                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="text-sm text-gray-900">{{ formatNumber(event.last_month) }}</div>
                </td>

                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="text-sm text-gray-900">{{ event.properties ? event.properties.length : 0 }}</div>
                </td>

                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="text-sm text-gray-900">
                    <div>{{ formatRetention(event.retention).days }}</div>
                    <div v-if="formatRetention(event.retention).months" class="text-xs text-gray-500">
                      ({{ formatRetention(event.retention).months }})
                    </div>
                  </div>
                </td>

                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <el-dropdown trigger="click" @command="handleDropdownCommand">
                    <el-button size="small" type="text" class="text-gray-500 hover:text-gray-700">
                      <i class="fa fa-ellipsis-v"></i>
                    </el-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <el-dropdown-item :command="{ action: 'edit-retention', event: event }">
                          <i class="fa fa-clock-o mr-2"></i>Change Retention Period
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- Retention Edit Dialog -->
    <el-dialog
      v-model="retentionDialogVisible"
      :title="'Retention Period - ' + selectedEvent?.event"
      width="500px"
      :before-close="cancelRetentionEdit"
      class="retention-dialog"
    >
      <div class="dialog-content">
        <!-- Input Section -->
        <div class="my-6 mx-4">
          <label class="block text-sm font-semibold text-gray-700 mb-3">New Retention Period</label>
          <div class="relative">
            <el-input
              v-model="newRetentionValue"
              type="number"
              placeholder="Enter number of days"
              min="1"
              max="1825"
              size="large"
              class="retention-input"
            >
              <template #suffix>
                <span class="text-gray-500 font-medium">days</span>
              </template>
            </el-input>
          </div>
          <p class="text-xs text-gray-500 mt-2 flex items-center">
            <i class="fa fa-info-circle mr-1"></i>
            Minimum: 1 day, Maximum: 1825 days (5 years)
          </p>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end space-x-3 py-0 border-gray-200">
          <el-button
            type="primary"
            @click="saveRetentionPeriod"
            :loading="savingRetention"
            :disabled="!newRetentionValue || isNaN(newRetentionValue) || newRetentionValue < 1 || newRetentionValue > 1825"
            size="small"
          >
            {{ savingRetention ? 'Saving...' : 'Save Changes' }}
          </el-button>
          <el-button @click="cancelRetentionEdit" type="danger" size="small" plain>
            Cancel
          </el-button>
        </div>
      </template>
    </el-dialog>
  </LayoutPage>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import LayoutPage from '@/core/components/LayoutPage.vue'
import Spinner from '@/core/components/Spinner.vue'
import LoadErrorTemp from '@/core/components/LoadErrorTemp.vue'
import { fetEventListWithRetention, updateEventRetention } from '@/services/dataRetentionService'

export default {
  name: 'DataRetentionPage',
  components: {
    LayoutPage,
    Spinner,
    LoadErrorTemp
  },
  setup() {
    // Reactive state
    const loading = ref(false)
    const loadError = ref(false)
    const events = ref([])
    const sortField = ref('this_month')
    const sortDirection = ref('desc')

    // Dialog state for retention editing
    const retentionDialogVisible = ref(false)
    const selectedEvent = ref(null)
    const newRetentionValue = ref('')
    const savingRetention = ref(false)

    const filters = reactive({
      eventName: ''
    })

    // Computed properties
    const filteredEvents = computed(() => {
      if (!filters.eventName.trim()) {
        return events.value
      }

      const searchTerm = filters.eventName.toLowerCase().trim()
      return events.value.filter(event => event.event && event.event.toLowerCase().includes(searchTerm))
    })

    const sortedEvents = computed(() => {
      const eventsToSort = filteredEvents.value

      if (!sortField.value) return eventsToSort

      return [...eventsToSort].sort((a, b) => {
        let aValue = a[sortField.value]
        let bValue = b[sortField.value]

        // Handle special case for properties count
        if (sortField.value === 'properties_count') {
          aValue = a.properties ? a.properties.length : 0
          bValue = b.properties ? b.properties.length : 0
        }

        // Handle retention field - convert to number if it looks like a number, otherwise treat as string
        if (sortField.value === 'retention') {
          // Handle null/undefined values
          aValue = aValue || 'N/A'
          bValue = bValue || 'N/A'

          // Try to parse as numbers if they look numeric (e.g., "30", "60", "90")
          const aNum = Number(aValue)
          const bNum = Number(bValue)

          if (!isNaN(aNum) && !isNaN(bNum)) {
            aValue = aNum
            bValue = bNum
          }
        }

        // Convert to numbers for numeric fields
        if (sortField.value === 'this_month' || sortField.value === 'last_month' || sortField.value === 'properties_count') {
          aValue = Number(aValue) || 0
          bValue = Number(bValue) || 0
        }

        // Sort strings case-insensitively
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase()
          bValue = bValue.toLowerCase()
        }

        if (sortDirection.value === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
        }
      })
    })

    // Methods
    const fetchEvents = async () => {
      try {
        loading.value = true
        loadError.value = false

        const response = await fetEventListWithRetention()

        // Handle the new API response format
        if (Array.isArray(response.data)) {
          events.value = response.data
        } else {
          events.value = response.data.results || []
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch events:', error)
        loadError.value = true
        ElMessage.error('Failed to load events. Please try again.')
      } finally {
        loading.value = false
      }
    }

    const getThisMonthTotal = () => {
      return filteredEvents.value.reduce((total, event) => total + (Number(event.this_month) || 0), 0)
    }

    const getLastMonthTotal = () => {
      return filteredEvents.value.reduce((total, event) => total + (Number(event.last_month) || 0), 0)
    }

    const handleSearchChange = debounce(() => {
      // Search is now handled locally in computed properties
      // No need to make API calls
    }, 500)

    const sortBy = field => {
      if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortField.value = field
        sortDirection.value = 'asc'
      }
    }

    const refreshData = async () => {
      try {
        // Clear any search filters to show fresh data
        filters.eventName = ''

        // Fetch fresh data from API
        await fetchEvents()

        ElMessage.success('Data refreshed successfully')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to refresh data:', error)
        ElMessage.error('Failed to refresh data. Please try again.')
      }
    }

    // Dropdown and dialog methods
    const handleDropdownCommand = command => {
      if (command.action === 'edit-retention') {
        openRetentionDialog(command.event)
      }
    }

    const openRetentionDialog = event => {
      selectedEvent.value = event
      newRetentionValue.value = event.retention || ''
      retentionDialogVisible.value = true
    }

    const cancelRetentionEdit = () => {
      retentionDialogVisible.value = false
      selectedEvent.value = null
      newRetentionValue.value = ''
      savingRetention.value = false
    }

    const saveRetentionPeriod = async () => {
      try {
        savingRetention.value = true
        const retentionDays = parseInt(newRetentionValue.value)

        if (isNaN(retentionDays) || retentionDays < 1) {
          ElMessage.error('Please enter a valid number of days (minimum 1)')
          return
        }

        if (retentionDays > 1825) {
          ElMessage.error('Maximum retention period is 1825 days (5 years)')
          return
        }

        await updateEventRetention(selectedEvent.value.event, retentionDays)

        // Update the local data
        const eventIndex = events.value.findIndex(e => e.event === selectedEvent.value.event)
        if (eventIndex !== -1) {
          events.value[eventIndex].retention = retentionDays
        }

        cancelRetentionEdit()
        ElMessage.success('Retention period updated successfully')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update retention:', error)
        ElMessage.error('Failed to update retention period. Please try again.')
      } finally {
        savingRetention.value = false
      }
    }

    // Utility functions
    const formatNumber = num => {
      return new Intl.NumberFormat().format(num)
    }

    const formatRetention = days => {
      if (!days || days === 'N/A' || isNaN(days)) {
        return { days: 'N/A', months: null }
      }

      const numDays = Number(days)

      // Calculate months (approximate: 30 days = 1 month)
      const months = Math.floor(numDays / 30)

      const daysText = numDays === 1 ? '1 day' : `${numDays} days`
      const monthsText = months > 0 ? (months === 1 ? '1 month' : `${months} months`) : null

      return { days: daysText, months: monthsText }
    }

    // Debounce function
    function debounce(func, wait) {
      let timeout
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout)
          func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
      }
    }

    // Lifecycle
    onMounted(() => {
      fetchEvents()
    })

    return {
      loading,
      loadError,
      events,
      filteredEvents,
      sortedEvents,
      sortField,
      sortDirection,
      filters,
      retentionDialogVisible,
      selectedEvent,
      newRetentionValue,
      savingRetention,
      fetchEvents,
      handleSearchChange,
      sortBy,
      refreshData,
      formatNumber,
      formatRetention,
      handleDropdownCommand,
      openRetentionDialog,
      cancelRetentionEdit,
      saveRetentionPeriod,
      getThisMonthTotal,
      getLastMonthTotal
    }
  }
}
</script>

<style scoped>
.fa {
  font-family: 'Font Awesome 5 Free' !important;
  font-weight: 900;
}

/* Dialog Styling */
:deep(.retention-dialog) {
  border-radius: 12px;
  overflow: hidden;
}

:deep(.retention-dialog .el-dialog__header) {
  padding: 0;
  margin-right: 0;
  border-bottom: 1px solid #e5e7eb;
}

:deep(.retention-dialog .el-dialog__body) {
  padding: 24px;
}

:deep(.retention-dialog .el-dialog__footer) {
  padding: 0;
}

.dialog-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

.dialog-content {
  margin: 0;
}

.dialog-footer {
  background-color: #f8fafc;
  margin: 0 -24px -24px -24px;
  padding: 16px 24px;
}
</style>
